<template>
  <b-form-group class="accordion" role="tablist">
    <b-card no-body class="mb-1">
      <b-card-header header-tag="header" role="tab">
        <div style="cursor: pointer" @click="visible = !visible">MEASURED DRUG CONC</div>
      </b-card-header>
      <b-collapse v-model="visible" role="tabpanel">
        <b-card-body>
          <VForm ref="observer" v-slot="{ meta: formMeta }" as="div">
            <b-form @submit.prevent="addMeasured">
              <p class="text-lg text-black font-semibold">
                Enter date and time for measured concentration
              </p>
              <InputDateValidation
                :field-name="fieldName"
                field-title="Conc."
                field-placeholder="Measured concentration"
                :field-date="form.datetime"
                :field-form="form.amount"
                :field-unit="form.amountUnit"
                :unit-list="measuredUnits"
                :rules-list="rules"
                @save-input-data="loadInputData"
              ></InputDateValidation>
              <hr />
              <span class="flex flex-column align-content-center">
                <b-button
                  v-b-tooltip.hover="
                    'Add this measured plasma concentration to the dose history — used for Bayesian parameter estimation'
                  "
                  class="rounded-circle shadow bg-secondary"
                  :disabled="!formMeta.valid || !isFormValid"
                  @click="addMeasured"
                  ><PlusIcon></PlusIcon
                ></b-button>
              </span>
            </b-form>
          </VForm>
        </b-card-body>
      </b-collapse>
    </b-card>
  </b-form-group>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';
import { addDoseFeedback } from '@/components/services/common';
import { isValidDateValue } from '@/models/utils/datetime';
import { DoseTypes, units } from '@/models/utils/constants.js';
import { Dose } from '@/models/Dose';
import InputDateValidation from '@/components/common/InputDateValidation.vue';
import PlusIcon from 'vue-material-design-icons/Plus.vue';
import { useDosingStore } from '@/stores/dosing';
import { useToast } from 'vue-toastification';

defineOptions({ name: 'MeasuredInput' });

const emit = defineEmits<{ (e: 'switchTab', evt: any, tab: number): void }>();

const store = useDosingStore();
const visible = ref(false);
const toast = useToast();
const observer = ref<any>(null);

const fieldName = 'measured';
const measuredUnits = units.concentration;
const rules = { between: [0.1, 10000] };

function initForm() {
  return { amount: null as number | null, amountUnit: 'mg/L', datetime: null as Date | null };
}

const form = ref(initForm());
const isFormValid = computed(
  () => isValidDateValue(form.value.datetime) && form.value.amount !== null,
);

function loadInputData(event: any) {
  form.value = {
    ...form.value,
    datetime: event.datetime,
    amount: event.value,
    amountUnit: event.unit,
  };
}

function addMeasured($event: any) {
  if (isFormValid.value) {
    observer.value.validate().then(({ valid }: { valid: boolean }) => {
      if (valid) {
        const dose = new Dose({
          type: DoseTypes.CONC,
          datetime: new Date(form.value.datetime!),
          amount: form.value.amount,
          amountUnit: form.value.amountUnit,
          duration: null,
          durationUnit: null,
          _rowVariant: 'info',
        });
        const feedback = addDoseFeedback(store.addDose(dose), 'Measured concentrations added');
        emit('switchTab', $event, 1);
        toast[feedback.type](feedback.text);
      }
    });
  }
}
</script>

<style scoped></style>
